
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ParkingSpace, User, UserRole, PriorityCategory, ReleaseRecord, QueueEntry, Infringement, SystemSettings, NotificationRecipient } from '../types';
import { ICONS } from '../constants';
import { getAdminInsightReport, getImprovementInsights, AppPersona, TimeFrame } from '../services/geminiService';

interface AdminPanelProps {
  users: User[];
  spaces: ParkingSpace[];
  priorities: PriorityCategory[];
  releases: ReleaseRecord[];
  queue: QueueEntry[];
  infringements: Infringement[];
  recipients: NotificationRecipient[];
  settings: SystemSettings;
  persona: AppPersona;
  appPasswords: { app: string, admin: string };
  onUpdatePasswords: (p: { app?: string, admin?: string }) => void;
  onAddUser: (user: User) => void;
  onRemoveUser: (userId: string) => void;
  onUpdateUser: (user: User) => void;
  onAssignSpace: (spaceId: number, userId: string | undefined) => void;
  onAddPriority: (p: PriorityCategory) => void;
  onUpdatePriority: (p: PriorityCategory) => void;
  onRemovePriority: (id: string) => void;
  onResolveInfringement: (id: string) => void;
  onCancelClaim: (releaseId: string) => void;
  onRemoveQueueEntry: (entryId: string) => void;
  onToggleQueueVip: (entryId: string) => void;
  onAddSpace: (space: Omit<ParkingSpace, 'ownerName'>) => void;
  onRemoveSpace: (spaceId: number) => void;
  onAddRecipient: (r: NotificationRecipient) => void;
  onRemoveRecipient: (id: string) => void;
  onAdminBook?: (spaceId: number, date: string, userName: string) => void;
  onDeleteRelease?: (id: string) => void;
  onBulkUpdateUsers: (users: User[]) => void;
  onBulkUpdateSpaces: (spaces: ParkingSpace[]) => void;
  onImportState: (data: any) => void;
  onUpdateSettings: (s: SystemSettings) => void;
  onResetUserPassword: (userId: string) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  users, spaces, onAssignSpace, onRemoveUser, onRemoveSpace, onRemoveQueueEntry, 
  priorities, queue, infringements, onAddUser, onAddPriority, onResolveInfringement, 
  releases, onAdminBook, onDeleteRelease, persona, onImportState, settings, recipients,
  onResetUserPassword
}) => {
  const [tab, setTab] = useState<'USERS' | 'SPACES' | 'PRIORITIES' | 'QUEUE' | 'INFRINGEMENTS' | 'BOOKINGS' | 'STORAGE'>('SPACES');
  const [newUser, setNewUser] = useState({ name: '', role: 'OWNER' as UserRole });
  const [manualBooking, setManualBooking] = useState({ spaceId: '', userName: '', date: new Date().toISOString().split('T')[0] });

  const handleManualBookSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualBooking.spaceId && manualBooking.userName && manualBooking.date) {
      onAdminBook?.(parseInt(manualBooking.spaceId), manualBooking.date, manualBooking.userName);
      setManualBooking({ spaceId: '', userName: '', date: new Date().toISOString().split('T')[0] });
    }
  };

  const exportToJSON = () => {
    const data = { users, spaces, releases, queue, priorities, infringements, settings };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `spaceshare_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = () => {
    let rows = [["Type", "ID", "Col1", "Col2", "Col3", "Col4", "Col5", "Col6", "Col7"]];
    
    users.forEach(u => rows.push(["USER", u.id, u.name, u.role, u.assignedSpaceId?.toString() || "", u.password || "", "", "", ""]));
    spaces.forEach(s => rows.push(["SPACE", s.id.toString(), s.displayLabel, s.ownerId || "", "", "", "", "", ""]));
    releases.forEach(r => rows.push(["RELEASE", r.id, r.spaceId.toString(), r.date, r.isClaimed.toString(), r.claimedBy || "", "", "", ""]));
    priorities.forEach(p => rows.push(["PRIORITY", p.id, p.label, p.weight.toString(), "", "", "", "", ""]));
    queue.forEach(q => rows.push(["QUEUE", q.id, q.userName, q.date, q.requestedAt.toString(), JSON.stringify(q.priorityIds), JSON.stringify(q.preferredZones || []), "", ""]));
    infringements.forEach(i => rows.push(["INFRINGEMENT", i.id, (i.spaceId || "").toString(), i.spaceLabel, i.reportedBy, i.reportedAt.toString(), i.status, i.reason, i.description]));

    const csvContent = rows.map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `spaceshare_backup_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleJSONImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        onImportState(data);
        alert('JSON Backup imported successfully!');
      } catch (err) {
        alert('Invalid JSON backup file.');
      }
    };
    reader.readAsText(file);
  };

  const handleCSVImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').map(l => {
          // Simple CSV parser that handles quotes
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < l.length; i++) {
            const char = l[i];
            if (char === '"') {
              if (inQuotes && l[i+1] === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current);
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current);
          return result;
        });

        const newState: any = { 
          users: [], 
          spaces: [], 
          releases: [], 
          queue: [], 
          priorities: [], 
          infringements: [],
          settings: settings 
        };

        lines.forEach(cols => {
          if (cols[0] === 'USER') {
            newState.users.push({ id: cols[1], name: cols[2], role: cols[3], assignedSpaceId: cols[4] ? parseInt(cols[4]) : undefined, password: cols[5] });
          } else if (cols[0] === 'SPACE') {
            newState.spaces.push({ id: parseInt(cols[1]), displayLabel: cols[2], ownerId: cols[3] || undefined });
          } else if (cols[0] === 'RELEASE') {
            newState.releases.push({ id: cols[1], spaceId: parseInt(cols[2]), date: cols[3], isClaimed: cols[4] === 'true', claimedBy: cols[5] || undefined });
          } else if (cols[0] === 'PRIORITY') {
            newState.priorities.push({ id: cols[1], label: cols[2], weight: parseInt(cols[3]) });
          } else if (cols[0] === 'QUEUE') {
            newState.queue.push({ id: cols[1], userName: cols[2], date: cols[3], requestedAt: parseInt(cols[4]), priorityIds: JSON.parse(cols[5] || '[]'), preferredZones: JSON.parse(cols[6] || '[]') });
          } else if (cols[0] === 'INFRINGEMENT') {
            newState.infringements.push({ id: cols[1], spaceId: cols[2] ? parseInt(cols[2]) : undefined, spaceLabel: cols[3], reportedBy: cols[4], reportedAt: parseInt(cols[5]), status: cols[6] as any, reason: cols[7] as any, description: cols[8] || "" });
          }
        });

        if (newState.users.length > 0 || newState.spaces.length > 0) {
          onImportState(newState);
          alert('CSV Backup imported successfully!');
        } else {
          alert('Invalid or empty CSV file.');
        }
      } catch (err) {
        console.error(err);
        alert('Error parsing CSV file.');
      }
    };
    reader.readAsText(file);
  };

  const TabButton = ({ id, label }: { id: typeof tab, label: string }) => (
    <button 
      onClick={() => setTab(id)} 
      className={`px-6 py-3 rounded-xl font-bold text-sm transition-all shrink-0 ${tab === id ? 'bg-white text-accent-red shadow-sm' : 'text-slate-500 hover:text-accent-red'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-8 pb-10 text-black">
      <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 overflow-x-auto no-scrollbar scroll-smooth">
        <TabButton id="SPACES" label="Spaces" />
        <TabButton id="BOOKINGS" label="Bookings" />
        <TabButton id="USERS" label="Personnel" />
        <TabButton id="QUEUE" label="Queue" />
        <TabButton id="INFRINGEMENTS" label={`Complaints (${infringements.filter(i => i.status === 'PENDING').length})`} />
        <TabButton id="STORAGE" label="Backup & Restore" />
      </div>

      {tab === 'STORAGE' && (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600">
                <ICONS.Download className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">System Backup</h3>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Export all data</p>
              </div>
            </div>

            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-xs text-slate-500 font-medium leading-relaxed">
                Download a complete snapshot of the system state. JSON is recommended for full fidelity, while CSV allows for manual editing in spreadsheet software.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button 
                onClick={exportToJSON}
                className="w-full py-4 bg-brand-dark text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2"
              >
                <ICONS.Download className="w-4 h-4" />
                Download JSON
              </button>
              <button 
                onClick={exportToCSV}
                className="w-full py-4 bg-white border-2 border-slate-200 text-slate-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:border-accent-red transition-all flex items-center justify-center gap-2"
              >
                <ICONS.FileText className="w-4 h-4" />
                Download CSV
              </button>
            </div>
          </div>

          <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600">
                <ICONS.Upload className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">Restore System</h3>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Import data from a backup file</p>
              </div>
            </div>

            <div className="p-6 bg-amber-50 rounded-2xl border border-amber-100">
              <p className="text-xs text-amber-700 font-bold leading-relaxed">
                Warning: Importing a backup will overwrite all current system data.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleJSONImport}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                />
                <button className="w-full py-4 bg-amber-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-amber-600 transition-all flex items-center justify-center gap-2">
                  <ICONS.Upload className="w-4 h-4" />
                  Import JSON
                </button>
              </div>
              <div className="relative">
                <input 
                  type="file" 
                  accept=".csv" 
                  onChange={handleCSVImport}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                />
                <button className="w-full py-4 bg-white border-2 border-amber-200 text-amber-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:border-amber-500 transition-all flex items-center justify-center gap-2">
                  <ICONS.FileText className="w-4 h-4" />
                  Import CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'BOOKINGS' && (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
                <div>
                  <h3 className="text-lg font-black text-slate-900 tracking-tight">Advance Booking</h3>
                  <p className="text-xs text-slate-400 font-bold mt-1">Manual override</p>
                </div>
                <form onSubmit={handleManualBookSubmit} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 ml-1">Personnel</label>
                    <select value={manualBooking.userName} onChange={e => setManualBooking({...manualBooking, userName: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-50 rounded-xl font-bold text-xs" required>
                      <option value="">-- Select Member --</option>
                      {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 ml-1">Target Space</label>
                    <select value={manualBooking.spaceId} onChange={e => setManualBooking({...manualBooking, spaceId: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-50 rounded-xl font-bold text-xs" required>
                      <option value="">-- Select Space --</option>
                      {spaces.map(s => <option key={s.id} value={s.id}>{s.displayLabel}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 ml-1">Scheduled Date</label>
                    <input type="date" value={manualBooking.date} onChange={e => setManualBooking({...manualBooking, date: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-50 rounded-xl font-bold text-xs" required />
                  </div>
                  <button type="submit" className="w-full py-3 bg-accent-red text-white font-black rounded-xl text-xs hover:bg-black transition-all">
                    Confirm Booking
                  </button>
                </form>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-slate-50 bg-slate-50/50">
                  <h3 className="text-sm font-black text-slate-900">Live Registry</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100 text-[11px] font-bold text-slate-400">
                      <tr>
                        <th className="px-8 py-4">Date</th>
                        <th className="px-8 py-4">Space</th>
                        <th className="px-8 py-4">Status</th>
                        <th className="px-8 py-4">Claimant</th>
                        <th className="px-8 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {releases.length === 0 ? (
                        <tr><td colSpan={5} className="px-8 py-10 text-center text-slate-400 italic text-sm">No active reservations.</td></tr>
                      ) : releases.sort((a,b) => a.date.localeCompare(b.date)).map(r => {
                        const space = spaces.find(s => s.id === r.spaceId);
                        return (
                          <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-8 py-4 text-[11px] font-bold text-slate-500">
                              {new Date(r.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </td>
                            <td className="px-8 py-4">
                              <span className="bg-brand-dark text-white px-2 py-1 rounded text-[10px] font-bold">{space?.displayLabel}</span>
                            </td>
                            <td className="px-8 py-4">
                              {r.isClaimed ? (
                                <span className="text-emerald-600 text-xs font-bold">Allocated</span>
                              ) : (
                                <span className="text-amber-600 text-xs font-bold">Released</span>
                              )}
                            </td>
                            <td className="px-8 py-4 text-xs font-bold text-slate-700">
                              {r.isClaimed ? r.claimedBy : <span className="text-slate-300 italic">Open</span>}
                            </td>
                            <td className="px-8 py-4 text-right">
                              <button onClick={() => onDeleteRelease?.(r.id)} className="p-2 text-slate-300 hover:text-accent-red transition-all"><ICONS.Trash className="w-4 h-4" /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'SPACES' && (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
           <div className="flex justify-between items-end">
              <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">Space Management</h3>
                <p className="text-xs text-slate-400 font-bold mt-1">Registry of 30 designated owners</p>
              </div>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
             {spaces.map(s => (
               <div key={s.id} className={`p-6 rounded-3xl border-2 transition-all group ${s.ownerId ? 'bg-white border-slate-100 shadow-sm' : 'bg-red-50/20 border-dashed border-accent-red/30'}`}>
                 <div className="flex justify-between items-start mb-6">
                    <span className="text-[10px] font-bold bg-brand-dark text-white px-2 py-1 rounded-md">{s.displayLabel}</span>
                    <button onClick={() => onRemoveSpace(s.id)} className="opacity-0 group-hover:opacity-100 p-2 text-slate-300 hover:text-accent-red transition-all"><ICONS.Trash className="w-4 h-4" /></button>
                 </div>
                 <div className="space-y-3">
                   <label className="block text-xs font-bold text-slate-400 ml-1">Designated Personnel</label>
                   <select value={s.ownerId || ''} onChange={(e) => onAssignSpace(s.id, e.target.value || undefined)} className="w-full text-xs font-bold border-2 rounded-xl px-4 py-3 outline-none border-slate-50 bg-slate-50 focus:border-accent-red transition-all">
                     <option value="">-- No Assignment --</option>
                     {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                   </select>
                 </div>
               </div>
             ))}
           </div>
        </div>
      )}

      {tab === 'USERS' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <form onSubmit={(e) => { e.preventDefault(); onAddUser({ id: Math.random().toString(36).substr(2, 9), name: newUser.name, role: newUser.role }); setNewUser({name: '', role: 'OWNER'}); }} className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[240px] space-y-2">
              <label className="block text-xs font-bold text-slate-500 ml-1">Personnel Name</label>
              <input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl font-bold text-sm" placeholder="John Doe" required />
            </div>
            <div className="min-w-[160px] space-y-2">
              <label className="block text-xs font-bold text-slate-500 ml-1">System Role</label>
              <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value as UserRole})} className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl font-bold text-sm text-black">
                <option value="OWNER">Space Owner</option>
                <option value="QUEUE_LIST">General Staff</option>
                <option value="ADMIN">Administrator</option>
              </select>
            </div>
            <button type="submit" className="bg-accent-red text-white p-4 rounded-2xl hover:bg-black transition-all shadow-xl shadow-red-100 flex items-center justify-center shrink-0"><ICONS.Plus className="w-6 h-6" /></button>
          </form>
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-400">
                <tr><th className="px-8 py-6">Member</th><th className="px-8 py-6 text-right">Access Level</th><th className="px-8 py-6 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50/50 group">
                    <td className="px-8 py-6 text-sm font-bold text-slate-900">{u.name}</td>
                    <td className="px-8 py-6 text-right">
                      <span className="px-3 py-1.5 rounded-xl bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-widest">{u.role}</span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                        <button onClick={() => {
                          if (confirm(`Reset password for ${u.name} to 'spaceshare'?`)) {
                            onResetUserPassword(u.id);
                          }
                        }} className="text-slate-300 hover:text-emerald-500 p-2 transition-all" title="Reset Password"><ICONS.Lock className="w-4 h-4" /></button>
                        <button onClick={() => onRemoveUser(u.id)} className="text-slate-300 hover:text-accent-red p-2 transition-all" title="Remove User"><ICONS.Trash className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
